import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const SECTION = 'lanoel-cart-crosssell';

class LanoelCartXsell extends HTMLElement {
  #busy = false;
  #token = 0;

  connectedCallback() {
    this.load();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.removeEventListener('click', this.#onClick);
  }

  #onCartUpdate = (event) => {
    event.promise?.then(() => setTimeout(() => this.load(true), 50)).catch(() => {});
  };

  async #productIds(refresh) {
    if (!refresh) return (this.dataset.productIds || '').split(',').filter(Boolean);
    try {
      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      return [...new Set(cart.items.map((i) => String(i.product_id)))];
    } catch {
      return [];
    }
  }

  async #fetch(productId, intent) {
    const base = this.dataset.url || '/recommendations/products';
    const url = `${base}?product_id=${productId}&limit=10&intent=${intent}&section_id=${SECTION}`;
    const res = await fetch(url);
    if (!res.ok) return null;
    return new DOMParser().parseFromString(await res.text(), 'text/html').querySelector('.ln-xsell__panel');
  }

  async load(refresh = false) {
    const token = ++this.#token;
    const ids = await this.#productIds(refresh);
    const inCart = new Set(ids.map(String));
    let panel = null;
    const items = new Map();
    if (ids.length) {
      try {
        outer: for (const intent of ['complementary', 'related']) {
          for (const id of ids.slice(0, 3)) {
            const found = await this.#fetch(id, intent);
            if (!found) continue;
            panel ??= found;
            for (const li of found.querySelectorAll('.ln-xsell__item')) {
              const key = li.querySelector('[data-variant-id]')?.dataset.variantId;
              // never suggest something that is already in the cart
              if (!key || items.has(key) || inCart.has(li.dataset.productId)) continue;
              items.set(key, li);
              if (items.size >= 3) break outer;
            }
          }
        }
      } catch {
        panel = null;
      }
    }
    if (token !== this.#token) return;
    if (!panel || !items.size) {
      // no suggestions left -> remove heading/panel completely (host collapses via :empty)
      this.replaceChildren();
      this.hidden = true;
      return;
    }
    const list = panel.querySelector('.ln-xsell__list');
    list.replaceChildren(...items.values());
    panel.dataset.count = String(items.size);
    this.innerHTML = panel.outerHTML;
    this.hidden = false;
  }

  #onClick = async (event) => {
    const button = event.target.closest('.ln-xsell__add');
    if (!button || this.#busy) return;
    event.preventDefault();
    this.#busy = true;
    button.setAttribute('aria-busy', 'true');
    const label = button.querySelector('.ln-xsell__add-label');
    const original = label?.textContent;
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    const variantId = Number(button.dataset.variantId);
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sectionIds.join(',') }),
      }).then((r) => r.json());
      if (response.status) throw new Error(response.description || response.message || 'Add failed');
      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'lanoel-cart-xsell', itemCount: 1, sections: response.sections, didError: false },
      });
    } catch (error) {
      console.warn('[lanoel-cart-xsell]', error);
      deferred.reject(error);
      if (label && original) label.textContent = original;
    } finally {
      button.removeAttribute('aria-busy');
      this.#busy = false;
    }
  };
}

if (!customElements.get('lanoel-cart-xsell')) customElements.define('lanoel-cart-xsell', LanoelCartXsell);
